import { DateTime } from 'luxon';
import { createDefaultDateRange } from 'charts/common/filterValues';
import { toSelectedIds } from 'shared/domain/analytics/issueChartFilters';
import { ISSUE_DATE_DISPLAY_FORMAT } from 'shared/utils/date/dates';
import type { ChartFilter, DateRangeObject } from 'shared/types/analytics';
import { isDateRangeObject } from 'shared/types/analytics';
import { isNonEmptyArray } from 'shared/utils/array';
import { findById } from 'shared/utils/id/findById';
import { isDateRangeSharedFilter, type SectionSharedFilter } from './sharedFilters';

/** Day and month only, when the range ends in the year it starts. */
const SAME_YEAR_START_FORMAT = 'dd.MM';

/** `15.10 – 29.10.2026`, or both years when they differ; empty without a valid range. */
export function describeDateRange(value: unknown, timezone: string): string {
  if (!isDateRangeObject(value)) {
    return '';
  }
  const { from, to }: DateRangeObject = value;
  const start = DateTime.fromISO(from, { zone: timezone });
  const end = DateTime.fromISO(to, { zone: timezone });
  if (!start.isValid || !end.isValid) {
    return '';
  }
  const startFormat = start.year === end.year ? SAME_YEAR_START_FORMAT : ISSUE_DATE_DISPLAY_FORMAT;
  return `${start.toFormat(startFormat)} – ${end.toFormat(ISSUE_DATE_DISPLAY_FORMAT)}`;
}

/** Whether a multiselect section filter narrows the charts (something is chosen). */
export function isMultiselectFilterActive(value: unknown): boolean {
  return isNonEmptyArray(toSelectedIds(value));
}

/**
 * The chosen values of a multiselect filter as a chip shows them: the first
 * label, then how many more (`QA +2`).
 */
export function describeSelection(
  filter: ChartFilter<unknown>,
  value: unknown,
  localizeLabel: (id: string, fallback?: string) => string
): string {
  const [firstId, ...otherIds] = toSelectedIds(value);
  if (firstId === undefined) {
    return '';
  }
  const first = localizeLabel(firstId, findById(filter.available, firstId, 'describeSelection')?.label);
  return isNonEmptyArray(otherIds) ? `${first} +${otherIds.length}` : first;
}

/**
 * Whether a section filter narrows the charts: something chosen in a
 * multiselect, or a date range other than the default last 14 days.
 */
export function isSectionFilterActive(shared: SectionSharedFilter, value: unknown, timezone: string): boolean {
  if (!isDateRangeSharedFilter(shared)) {
    return isMultiselectFilterActive(value);
  }
  const { from, to } = createDefaultDateRange(timezone);
  return isDateRangeObject(value) && (value.from !== from || value.to !== to);
}

/** The value a cleared section filter gets: nothing chosen, or the default date range. */
export function clearedSectionFilterValue(shared: SectionSharedFilter, timezone: string): unknown {
  return isDateRangeSharedFilter(shared) ? createDefaultDateRange(timezone) : [];
}
